import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, toast } from '@etherisc/ui-kit';
import { DangerButton, PrimaryButton, NeutralButton } from '../../components/buttons';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
} from 'lucide-react';

// Define tables and their management rules
const TABLES = {
  // Critical tables - only reseed allowed
  users: { name: 'Users', critical: true },
  roles: { name: 'Roles', critical: true },
  permissions: { name: 'Permissions', critical: true },
  role_permissions: { name: 'Role-Permissions', critical: true },
  user_roles: { name: 'User-Roles', critical: true },
  organizations: { name: 'Organizations', critical: true },
  
  // Non-critical tables - both clear and reseed allowed
  sessions: { name: 'Sessions', critical: false },
  audit_logs: { name: 'Audit Logs', critical: false },
  products: { name: 'Products', critical: false },
  farmers: { name: 'Farmers', critical: false },
  quotes: { name: 'Quotes', critical: false },
  policies: { name: 'Policies', critical: false },
  claims: { name: 'Claims', critical: false },
  payments: { name: 'Payments', critical: false },
  blockchain_records: { name: 'Blockchain Records', critical: false },
  system_settings: { name: 'System Settings', critical: false },
};

export function DatabaseManagement() {
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleTableAction = async (action: 'clear' | 'reseed') => {
    if (!selectedTable) {
      toast.error('Please select a table first');
      return;
    }

    const table = TABLES[selectedTable as keyof typeof TABLES];
    
    // Validate action for critical tables
    if (table.critical && action === 'clear') {
      toast.error('Clear operation is not allowed for critical tables');
      return;
    }

    const confirmMessage = action === 'clear' 
      ? `Are you sure you want to CLEAR all data from the ${table.name} table? This action cannot be undone.`
      : `Are you sure you want to RE-SEED the ${table.name} table?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`/api/admin/database/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`, // Fixed: using correct auth token key
        },
        body: JSON.stringify({ table: selectedTable }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} table`);
      }

      const result = await response.json();
      
      toast.success(`Successfully ${action === 'clear' ? 'cleared' : 're-seeded'} ${table.name} table`, {
        description: result.message || `Operation completed successfully`
      });
      
    } catch (error) {
      console.error(`Error ${action}ing table:`, error);
      toast.error(`Failed to ${action} table`, {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const selectedTableInfo = selectedTable ? TABLES[selectedTable as keyof typeof TABLES] : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-3">
        <NeutralButton
          onClick={handleBackToDashboard}
          size="sm"
        >
          <ChevronLeftIcon size={16} />
          Back to Dashboard
        </NeutralButton>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Database Management</h1>
          <p className="text-gray-600 mt-2">
            Manage database tables with clear and re-seed operations. Use with caution.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Table Operations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Table Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Table
            </label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
            >
              <option value="">Choose a table to manage</option>
              {Object.entries(TABLES).map(([key, table]) => (
                <option key={key} value={key}>
                  {table.name} {table.critical ? '(Critical)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Table Info */}
          {selectedTableInfo && (
            <div className={`p-4 rounded-lg border ${
              selectedTableInfo.critical 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <h3 className="font-medium text-gray-900">{selectedTableInfo.name}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {selectedTableInfo.critical 
                  ? 'This is a critical table. Only re-seed operations are allowed to preserve data integrity.'
                  : 'This table supports both clear and re-seed operations.'
                }
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <DangerButton
              onClick={() => handleTableAction('clear')}
              disabled={!selectedTable || selectedTableInfo?.critical || isLoading}
            >
              {isLoading ? 'Processing...' : 'Clear Table'}
            </DangerButton>
            
            <PrimaryButton
              onClick={() => handleTableAction('reseed')}
              disabled={!selectedTable || isLoading}
            >
              {isLoading ? 'Processing...' : 'Re-Seed Table'}
            </PrimaryButton>
          </div>

          {/* Warning Notice */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Warning: Destructive Operations
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>Clear</strong>: Permanently deletes all data from the selected table</li>
                    <li><strong>Re-Seed</strong>: Resets table with default/demo data</li>
                    <li>These operations cannot be undone</li>
                    <li>Always backup your database before performing these operations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 